import PropTypes from 'prop-types'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { Trans, useTranslation } from 'react-i18next'
import classnames from 'classnames'
import { toast } from 'react-toastify'
import { Col, Row } from 'reactstrap'
import { Link } from 'react-router-dom'
import Embed from 'react-tiny-oembed'

import { RegisterButton } from './ProgramPresentationPage'
import CommunityContactModal from '../../../community-contact-modal/CommunityContactModal'
import JoinWaitlistButton from './JoinWaitlistButton'
import WhyTwoChargesModal from './modals/WhyTwoChargesModal'

import styles from './ProgramActionsCard.module.scss'
import { ReactComponent as GolferIcon } from '../../../../assets/images/common/icons/golfer-half-icon.svg'
import { ReactComponent as CallIcon } from '../../../../assets/images/common/icons/call-icon.svg'
import { ReactComponent as MailIcon } from '../../../../assets/images/common/icons/mail-icon.svg'
import { ReactComponent as ShareIcon } from '../../../../assets/images/common/icons/share-icon.svg'
import { ReactComponent as InfoCircleIcon } from '../../../../assets/images/common/icons/info-circle-icon-outline.svg'
import CommunityAvatarPlaceholder from '../../../../assets/images/mock/classRowBanner.png'
import useJoinWaitlist from './hooks/useJoinWaitlist'
import { canDisplaySpotsAvailableOnSignup } from '../../../../utility/programHelper'
import { canContactCommunity } from '../../../../utility/userHelper'
import { displayTotals, displayWithCurrency, totalsByCurrency } from '../../../../utility/currency'
import { selectMembershipCovered } from '../../../../reducers/programRegistrationReducer'


const MembershipDescription = ({ covered, cheapestPlan, family }) => {
  const { t } = useTranslation()

  if (!covered) {
    return (
      <>
        <p className={styles['fee-title']}>{t('programs.landing.actionsCard.membership')}</p>
        {!!cheapestPlan && (
          <p className={styles['fee-subtitle']}>
            {t('programs.landing.actionsCard.membershipCovers', {
              tier: t(`programs.registration.membershipPlan.tiers.${cheapestPlan.tier}`),
              count: cheapestPlan.seats,
            })}
          </p>
        )}
      </>
    )
  }

  const sharedSeats = family.seatsTotal > 1
  const tier = t(`programs.registration.membershipPlan.tiers.${family.tier}`)

  return (
    <>
      <p className={styles['fee-title']}>
        {sharedSeats
          ? t('programs.landing.actionsCard.membershipWithTier', { tier })
          : t('programs.landing.actionsCard.membership')}
      </p>
      <p className={styles['fee-subtitle']}>
        {sharedSeats
          ? t('programs.landing.actionsCard.membershipCoversUpTo', { count: family.seatsTotal })
          : t('programs.landing.actionsCard.membershipCovers', { tier, count: family.seatsTotal })}
      </p>
    </>
  )
}

const MembershipStatus = ({ covered }) => {
  const { t } = useTranslation()
  const [chargesInfoOpen, setChargesInfoOpen] = useState(false)

  return (
    <div className={styles['membership-status']}>
      <span className={classnames(styles['membership-pill'], { [styles['pill-active']]: covered })}>
        {covered ? t('programs.landing.actionsCard.membershipAlreadyActive') : t('programs.landing.actionsCard.membershipRequired')}
      </span>
      <button
        type='button'
        className={styles['charges-info-button']}
        onClick={() => setChargesInfoOpen(true)}
        aria-label={t('programs.landing.whyTwoCharges.title')}
      >
        <InfoCircleIcon aria-hidden='true' className={styles['charges-info-icon']} />
      </button>
      <WhyTwoChargesModal isOpen={chargesInfoOpen} onClose={() => setChargesInfoOpen(false)} />
    </div>
  )
}

const PricingSummary = () => {
  const { t } = useTranslation()
  const course = useSelector(state => state.programPresentation.course)
  const membership = course.membership
  const hasPackages = !!course.packages?.length
  const covered = useSelector(selectMembershipCovered)

  if (!hasPackages && !membership) {
    return null
  }

  const cheapestPlan = membership?.plans?.[0]
  const lowestPackagePrice = hasPackages ? Math.min(...course.packages.map(p => p.price)) : null
  // a covered family sees what its own tier costs rather than the cheapest one, struck through below
  const pricedPlan = covered ? membership.plans?.find(plan => plan.tier === membership.family.tier) : cheapestPlan
  const planPrice = pricedPlan ? displayWithCurrency(pricedPlan.amount, pricedPlan.currency) : null
  const lines = []

  if (hasPackages) {
    lines.push({ amount: lowestPackagePrice, currency: course.community.currency })
  }
  if (!covered && cheapestPlan) {
    lines.push({ amount: cheapestPlan.amount, currency: cheapestPlan.currency })
  }
  if (covered && !lines.length) {
    lines.push({ amount: 0, currency: course.community.currency })
  }

  return (
    <div className='px-4'>
      <div className={styles['pricing-summary']}>
        <div className={styles['what-youll-pay-row']}>
          <p className={styles['what-youll-pay-label']}>{t('programs.landing.actionsCard.whatYoullPay')}</p>
          <p className={styles['price-per-player']}>
            {!!lines.length && (
              <Trans
                i18nKey='programs.landing.actionsCard.perPlayer'
                components={{ suffix: <span className={styles['per-player-suffix']} /> }}
                values={{ price: displayTotals(totalsByCurrency(lines)) }}
              />
            )}
          </p>
        </div>
        {hasPackages && (
          <div className={styles['coaching-fee-row']}>
            <div>
              <p className={styles['fee-title']}>{t('programs.landing.actionsCard.programCoachingFee')}</p>
              <p className={styles['fee-subtitle']}>{t('programs.landing.actionsCard.pricingVariesByPackage')}</p>
            </div>
            <span className={styles['fee-price']}>{displayWithCurrency(lowestPackagePrice, course.community.currency)}</span>
          </div>
        )}
        {!!membership && (
          <div className={styles['membership-fee-row']}>
            <div>
              <MembershipDescription covered={covered} cheapestPlan={cheapestPlan} family={membership.family} />
              <MembershipStatus covered={covered} />
            </div>
            {!!planPrice && (
              <span className={classnames(styles['fee-price'], { [styles['fee-price-waived']]: covered })}>
                {t('programs.landing.actionsCard.membershipPerYear', { price: planPrice })}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

const OpenSpotsCounter = ({ inline }) => {
  const course = useSelector(state => state.programPresentation.course)

  if (!canDisplaySpotsAvailableOnSignup(course)) {
    return null
  }

  if (inline) {
    return (
      <p className={classnames('mb-0 d-flex align-items-center', styles['open-spots-line'])}>
        <GolferIcon className={styles.icon} />
        <span className='ml-1'>
          <Trans
            i18nKey='programs.landing.actionsCard.openSpots'
            components={{ number: <span /> }}
            values={{ count: course.spotsAvailable }}
          />
        </span>
      </p>
    )
  }

  return (
    <h3 className={classnames('px-4 py-3 d-flex d-lg-none align-items-center justify-content-center', styles['program-spots-left'])}>
      <GolferIcon className={styles.icon} />
      <span className='ml-2 text-center'>
        <Trans
          i18nKey='programs.landing.actionsCard.openSpots'
          components={{ number: <span className={styles['open-spots-number']} /> }}
          values={{ count: course.spotsAvailable }}
        />
      </span>
    </h3>
  )
}

const ProgramActionsCard = ({ className }) => {
  const { t } = useTranslation()
  const { handleJoinButtonClick } = useJoinWaitlist()
  const [isModalOpen, setModalOpen] = useState(false)

  const course = useSelector(state => state.programPresentation.course)
  const user = useSelector(state => state.user.current_user)
  const community = course.community
  const isPhoneNumberPresent = !!community.phone
  const showContactButton = canContactCommunity(user, community.id)
  const landingPageUrl = `${process.env.REACT_APP_BASE_URL}/landing/${community.id}/class/${course.id}`


  const handleClickShareButton = () => {
    navigator.clipboard.writeText(landingPageUrl)
      .then(() => {
        toast.info(t('programs.landing.actionsCard.linkCopied'), {
          toastId: 'LinkCopiedToast',
          position: 'bottom-right',
          autoClose: 2500,
        })
      })
  }

  return (
    <>
      <div className={classnames('flex-column pt-lg-3 pb-lg-3 justify-content-between', styles['program-actions-card'], !!className && className)}>
        {!!course.personalizedVideoUrl && (
          <div className={classnames('mt-3 mt-lg-0 mb-lg-4 mx-4', styles['video-wrapper'])}>
            <Embed url={course.personalizedVideoUrl} />
          </div>
        )}
        <Link
          to={`/landing/${community.id}`}
          className='d-none px-4 d-lg-flex justify-content-start align-items-center'
          onClick={(e) => course.previewMode && e.preventDefault()}
        >
          <div className={classnames('round-image-wrapper', styles['light-navy-border'], styles['community-avatar'])}>
            <img
              className='round-image'
              src={community.photo ? community.photo.fileUrl : CommunityAvatarPlaceholder}
              alt={t('programs.landing.actionsCard.communityAlt')}
            />
          </div>
          <div className={classnames('ml-2', styles['community-info'])}>
            <h2 className={classnames('mb-0', styles['program-community-name'])} title={course?.community?.name}>{community.name}</h2>
            <OpenSpotsCounter inline />
          </div>
        </Link>
        <OpenSpotsCounter />
        <PricingSummary />
        <div className={classnames('px-5 py-3', styles['program-contact-share-actions'])}>
          <Row>
            {isPhoneNumberPresent && (
              <Col xs={6} md={showContactButton ? 4 : 6} className='d-md-flex align-items-center'>
                <a
                  href={`tel:${community.phone_prefix || ''}${community.phone}`}
                  className={classnames('w-100 d-flex animation-shrink align-items-center', styles['program-action'])}
                >
                  <CallIcon className={styles.icon} />
                  <span className='ml-2'>{t('programs.landing.actionsCard.callUs')}</span>
                </a>
              </Col>
            )}
            {showContactButton && (
              <Col xs={6} md={isPhoneNumberPresent ? 4 : 6} className='d-md-flex align-items-center'>
                <button
                  onClick={(e) => course.previewMode ? e.preventDefault() : setModalOpen(true)}
                  className={classnames(
                    'w-100 d-flex animation-shrink align-items-center justify-content-md-center',
                    styles['program-action'],
                    { 'justify-content-end': isPhoneNumberPresent }
                  )}
                >
                  <MailIcon className={styles.icon} />
                  <span className='ml-2'>{t('programs.landing.actionsCard.sendEmail')}</span>
                </button>
              </Col>
            )}
            <Col
              xs={isPhoneNumberPresent && showContactButton ? 12 : showContactButton || isPhoneNumberPresent ? 6 : 12}
              md={isPhoneNumberPresent && showContactButton ? 4 : 6}
              className={classnames('d-md-flex align-items-center', { 'pt-2 pt-md-0': isPhoneNumberPresent && showContactButton })}
            >
              <button
                className={classnames(
                  'w-100 d-flex animation-shrink align-items-center',
                  styles['program-action'],
                  isPhoneNumberPresent ? 'justify-content-center justify-content-md-end' : 'justify-content-end justify-content-lg-center'
                )}
                onClick={(e) => course.previewMode ? e.preventDefault() : handleClickShareButton()}
              >
                <ShareIcon className={styles.icon} />
                <span className='ml-2'>{t('programs.landing.actionsCard.copyAndShare')}</span>
              </button>
            </Col>
          </Row>
          <Row className='mt-3 d-none d-lg-flex'>
            {course.canStudentsJoinWaitlist
              ? <JoinWaitlistButton onJoinButtonClick={handleJoinButtonClick} />
              : <RegisterButton />}
          </Row>
        </div>
      </div>
      {showContactButton && (
        <CommunityContactModal
          communityId={community.id}
          communityName={community.name}
          isOpen={isModalOpen}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  )
}

MembershipDescription.propTypes = {
  covered: PropTypes.bool.isRequired,
  cheapestPlan: PropTypes.shape({ tier: PropTypes.string, seats: PropTypes.number }),
  family: PropTypes.shape({ tier: PropTypes.string, seatsTotal: PropTypes.number }),
}

MembershipStatus.propTypes = {
  covered: PropTypes.bool.isRequired,
}

OpenSpotsCounter.propTypes = {
  inline: PropTypes.bool,
}

ProgramActionsCard.propTypes = {
  className: PropTypes.string,
}

export default ProgramActionsCard
